/**
 * SpellSystem.ts — Jim's six spells (Stage 15).
 *
 * All spells are cast through Jim (bandClass === 'mystic') or Keyboard
 * Prophet recruits.  If Jim goes down the whole system locks out for
 * SPELL_LOCKOUT_TIME seconds.
 *
 * Spell roster:
 *   Signal Boost    — doubles Beacon signal output for 30 s         (cd 90 s)
 *   Temporal Echo   — slows all nearby enemies 50% for 15 s         (cd 60 s)
 *   Crowd Confusion — nearest enemy attacks its own side for 10 s   (cd 45 s)
 *   Feedback Loop   — AoE damage; bonus vs Streaming Moguls         (cd 120 s)
 *   Prophecy Reveal — reveals next wave direction and timing        (cd 180 s)
 *   Bassline Summon — emergency Sales pulse                         (cd 180 s)
 */

import { Faction }             from '../types';
import type { Unit }           from '../entities/Unit';
import type { BeaconSystem }   from './BeaconSystem';
import type { ResourceSystem } from './ResourceSystem';
import {
  SPELL_LOCKOUT_TIME,
  SPELL_SIGNAL_BOOST_DUR,
  SPELL_TEMPORAL_ECHO_DUR,
  SPELL_TEMPORAL_ECHO_RAD,
  SPELL_CROWD_CONFUSE_DUR,
  SPELL_FEEDBACK_RAD,
  SPELL_FEEDBACK_DMG,
  SPELL_FEEDBACK_MOGUL_MULT,
  SPELL_BASSLINE_SALES,
  SPELL_CD_SIGNAL_BOOST,
  SPELL_CD_TEMPORAL_ECHO,
  SPELL_CD_CROWD_CONFUSE,
  SPELL_CD_FEEDBACK_LOOP,
  SPELL_CD_PROPHECY_REVEAL,
  SPELL_CD_BASSLINE_SUMMON,
} from '../constants';

// ── Types ─────────────────────────────────────────────────────────────────────

export type SpellKey =
  | 'signalBoost'
  | 'temporalEcho'
  | 'crowdConfusion'
  | 'feedbackLoop'
  | 'prophecyReveal'
  | 'basslineSummon';

export interface SpellDef {
  key:      SpellKey;
  name:     string;
  desc:     string;
  cooldown: number;
  pulse:    number;
}

export const SPELLS: readonly SpellDef[] = [
  { key: 'signalBoost',    name: 'Signal Boost',    desc: 'Double signal output for 30s',       cooldown: SPELL_CD_SIGNAL_BOOST,    pulse: 0x00ffcc },
  { key: 'temporalEcho',   name: 'Temporal Echo',   desc: 'Slow nearby enemies 50% for 15s',    cooldown: SPELL_CD_TEMPORAL_ECHO,   pulse: 0xaa44ff },
  { key: 'crowdConfusion', name: 'Crowd Confusion', desc: 'Nearest enemy fights its own side',  cooldown: SPELL_CD_CROWD_CONFUSE,   pulse: 0xff44aa },
  { key: 'feedbackLoop',   name: 'Feedback Loop',   desc: 'AoE damage — bonus vs Moguls',       cooldown: SPELL_CD_FEEDBACK_LOOP,   pulse: 0xff8833 },
  { key: 'prophecyReveal', name: 'Prophecy Reveal', desc: 'Reveal next wave intel',             cooldown: SPELL_CD_PROPHECY_REVEAL, pulse: 0x4488ff },
  { key: 'basslineSummon', name: 'Bassline Summon', desc: `Emergency +${SPELL_BASSLINE_SALES} Sales`, cooldown: SPELL_CD_BASSLINE_SUMMON, pulse: 0x44cc66 },
];

export interface SpellContext {
  caster:       Unit;
  allUnits:     Unit[];
  beacon:       BeaconSystem;
  resources:    ResourceSystem;
  nextWaveInfo: () => string;
  triggerPulse: (colour: number, duration?: number) => void;
  showToast:    (msg: string, sev: 'info' | 'success' | 'warning' | 'danger') => void;
}

// ── System ────────────────────────────────────────────────────────────────────

export class SpellSystem {
  private readonly cooldowns = new Map<SpellKey, number>();
  private jimLockoutTimer = 0;

  update(delta: number): void {
    for (const [key, cd] of this.cooldowns) {
      if (cd > 0) this.cooldowns.set(key, Math.max(0, cd - delta));
    }
    if (this.jimLockoutTimer > 0) {
      this.jimLockoutTimer = Math.max(0, this.jimLockoutTimer - delta);
    }
  }

  /** Call when Jim goes down — locks all spells for SPELL_LOCKOUT_TIME seconds. */
  onJimDown(): void {
    this.jimLockoutTimer = SPELL_LOCKOUT_TIME;
  }

  get isLocked(): boolean { return this.jimLockoutTimer > 0; }
  get lockoutSecsRemaining(): number { return this.jimLockoutTimer; }

  canCast(key: SpellKey): boolean {
    return !this.isLocked && (this.cooldowns.get(key) ?? 0) <= 0;
  }

  cooldownFor(key: SpellKey): number {
    return this.cooldowns.get(key) ?? 0;
  }

  cast(key: SpellKey, ctx: SpellContext): boolean {
    if (!this.canCast(key)) return false;

    switch (key) {
      case 'signalBoost':    castSignalBoost(ctx);    break;
      case 'temporalEcho':   castTemporalEcho(ctx);   break;
      case 'crowdConfusion': castCrowdConfusion(ctx); break;
      case 'feedbackLoop':   castFeedbackLoop(ctx);   break;
      case 'prophecyReveal': castProphecyReveal(ctx); break;
      case 'basslineSummon': castBasslineSummon(ctx); break;
      default: return false;
    }

    const def = SPELLS.find(s => s.key === key)!;
    this.cooldowns.set(key, def.cooldown);
    ctx.triggerPulse(def.pulse, 2.5);
    return true;
  }

  getState() {
    return {
      cooldowns:       Object.fromEntries(this.cooldowns) as Record<string, number>,
      jimLockoutTimer: this.jimLockoutTimer,
    };
  }

  restoreState(s: ReturnType<SpellSystem['getState']>): void {
    this.cooldowns.clear();
    for (const [k, v] of Object.entries(s.cooldowns ?? {})) {
      this.cooldowns.set(k as SpellKey, v);
    }
    this.jimLockoutTimer = s.jimLockoutTimer ?? 0;
  }
}

// ── Spell implementations ─────────────────────────────────────────────────────

function castSignalBoost(ctx: SpellContext): void {
  ctx.beacon.activateSignalBoost(SPELL_SIGNAL_BOOST_DUR);
  ctx.showToast('◈ Signal Boost — Beacon output doubled for 30s!', 'success');
}

function castTemporalEcho(ctx: SpellContext): void {
  let count = 0;
  for (const u of ctx.allUnits) {
    if (!u.alive || u.isDowned || u.faction === Faction.Player) continue;
    if (spellDist(ctx.caster, u) <= SPELL_TEMPORAL_ECHO_RAD) {
      u.moveSlowTimer = Math.max(u.moveSlowTimer, SPELL_TEMPORAL_ECHO_DUR);
      count++;
    }
  }
  ctx.showToast(
    count > 0
      ? `◈ Temporal Echo — ${count} enemies slowed for 15s`
      : '◈ Temporal Echo — no enemies in range',
    count > 0 ? 'success' : 'info',
  );
}

function castCrowdConfusion(ctx: SpellContext): void {
  const target = nearestEnemy(ctx.caster, ctx.allUnits);
  if (!target) {
    ctx.showToast('◈ Crowd Confusion — no target in range', 'info');
    return;
  }
  target.confusedTimer = SPELL_CROWD_CONFUSE_DUR;
  ctx.showToast(`◈ Crowd Confusion — ${target.displayName} is fighting its own side!`, 'success');
}

function castFeedbackLoop(ctx: SpellContext): void {
  let count = 0;
  for (const u of ctx.allUnits) {
    if (!u.alive || u.isDowned || u.faction === Faction.Player) continue;
    if (spellDist(ctx.caster, u) > SPELL_FEEDBACK_RAD) continue;
    const dmg = u.enemyType === 'StreamingMogul'
      ? Math.round(SPELL_FEEDBACK_DMG * SPELL_FEEDBACK_MOGUL_MULT)
      : SPELL_FEEDBACK_DMG;
    u.hp = Math.max(1, u.hp - dmg);
    u.hitFlash = 0.35;
    count++;
  }
  ctx.showToast(
    count > 0
      ? `◈ Feedback Loop — ${count} enemies struck`
      : '◈ Feedback Loop — no enemies in range',
    count > 0 ? 'success' : 'info',
  );
}

function castProphecyReveal(ctx: SpellContext): void {
  ctx.showToast(`◈ Prophecy Reveal — ${ctx.nextWaveInfo()}`, 'info');
}

function castBasslineSummon(ctx: SpellContext): void {
  ctx.resources.add('sales', SPELL_BASSLINE_SALES);
  ctx.showToast(`◈ Bassline Summon — +${SPELL_BASSLINE_SALES} Sales injected!`, 'success');
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function spellDist(a: Unit, b: Unit): number {
  return Math.hypot(a.gridPos.col - b.gridPos.col, a.gridPos.row - b.gridPos.row);
}

function nearestEnemy(caster: Unit, allUnits: Unit[]): Unit | null {
  let best: Unit | null = null;
  let bestD = Infinity;
  for (const u of allUnits) {
    if (!u.alive || u.isDowned || u.faction === Faction.Player) continue;
    const d = spellDist(caster, u);
    if (d < bestD) { best = u; bestD = d; }
  }
  return best;
}
